/**
 * onePagerRequestMapper.ts
 * Centralizes all mapping logic from frontend state → backend request body.
 * Keeps useOnePager and onePagerApi free of transformation concerns.
 */

import { GrainKey, TableSortState } from '../constants/onePagerConstants';
import {
	ChartConfiguration,
	OnePagerFilters,
	OnePagerGrouping,
	OnePagerRequest,
	OnePagerSortConfig,
	OnePagerSortDirection,
} from '../types/onePagerTypes';

// ─── Grain → Backend Field Mapping ───────────────────────────────────────────

/** Maps frontend GrainKey to the backend field name expected by the API */
const GRAIN_TO_FIELD: Record<GrainKey, string> = {
	rg: 'REGION',
	mk: 'MARKET',
	lc: 'LOCATION',
	lt: 'LOCATION_TYPE',
	yr: 'YEAR',
	pd: 'PERIOD',
	wk: 'WEEK',
	dt: 'DATE',
};

// ─── KPI Column → Backend Field Mapping ──────────────────────────────────────

/** Maps frontend PAGER_COLUMNS IDs to backend metric field names for sorting */
const KPI_TO_FIELD: Record<string, string> = {
	// Safety
	rir: 'rir',
	// People
	gap: 'gapTime',
	kcph: 'kcph',
	lcph: 'layerCph',
	ot: 'ot',
	ttp: 'timeToPunch',
	turn: 'turnOver',
	undir: 'undirectedHours',
	// Accuracy
	bpa: 'bpa',
	ihc: 'ihc',
	pte_execution: 'perfectTruckExecution',
	sia: 'sia',
	// Reliability
	fill: 'fillRate',
	haul: 'haulback',
	ontime: 'onTime',
	// Costs
	dps: 'dps',
	layer: 'layer',
	pku: 'pku',
	pte: 'pte',
	repack: 'repackDollar',
	waste: 'allInWaste',
};

/**
 * Convert grain selections into the backend groupings array.
 * Each grain becomes a grouping entry with a 1-based display priority
 * representing the grouping hierarchy (1 = first grouping, 2 = second, etc.).
 */
export function buildGroupings(grainSelections: readonly GrainKey[]): OnePagerGrouping[] {
	return grainSelections
		.filter((key) => GRAIN_TO_FIELD[key])
		.map((key, index) => ({
			field: GRAIN_TO_FIELD[key],
			order: index + 1,
		}));
}

/**
 * Convert the Redux TableSortState into the backend sort config.
 * Returns undefined if sort is on the default 'geo' column (grain-based sort
 * is handled via groupings order, not the sort field).
 *
 * Grain columns must be translated through GRAIN_TO_FIELD — a raw
 * toUpperCase() would emit meaningless fields like "RG" instead of "REGION".
 * 
 * KPI columns are mapped through KPI_TO_FIELD to convert column IDs
 * (like 'pte_execution') to backend field names (like 'perfectTruckExecution').
 */
export function buildSortConfig(sort: TableSortState): OnePagerSortConfig | undefined {
	if (!sort.col || sort.col === 'geo') {
		return undefined;
	}

	const grainField = GRAIN_TO_FIELD[sort.col as GrainKey];
	const kpiField = KPI_TO_FIELD[sort.col];

	return {
		// Grain columns use GRAIN_TO_FIELD mapping (e.g., 'rg' → 'REGION')
		// KPI columns use KPI_TO_FIELD mapping (e.g., 'pte_execution' → 'perfectTruckExecution')
		// Unknown columns pass through as-is for backward compatibility
		field: grainField ?? kpiField ?? sort.col,
		direction: sort.dir === 'asc' ? 'ASC' : 'DESC',
	};
}


// ─── Main Request Builder ─────────────────────────────────────────────────────

export interface BuildOnePagerRequestParams {
	readonly chartConfiguration: ChartConfiguration;
	readonly filters: OnePagerFilters;
	readonly sort: TableSortState;
	readonly page: number;
	readonly size: number;
}

/**
 * Builds the complete flat request body for POST /api/one-pager.
 * This is the single place where all frontend state is transformed
 * into the backend contract.
 */
export function buildOnePagerRequest(params: BuildOnePagerRequestParams): OnePagerRequest {
	const { chartConfiguration, filters, sort, page, size } = params;

	const request: OnePagerRequest = {
		// Filters (flat)
		region: [...filters.region],
		market: [...filters.market],
		location: [...filters.location],
		locationType: [...filters.locationType],
		year: [...filters.year],
		period: [...filters.period],
		week: [...filters.week],

		// Groupings from chart configuration
		groupings: buildGroupings(chartConfiguration.grainSelections),

		// Pagination
		page,
		size,
	};

	// Optional date range
	if (filters.startDate) {
		request.startDate = filters.startDate;
	}
	if (filters.endDate) {
		request.endDate = filters.endDate;
	}

	// Optional sorting
	const sortConfig = buildSortConfig(sort);
	if (sortConfig) {
		request.sort = sortConfig;
	}

	return request;
}
